// "Angaben zur Organisation" in der Karte "Neu" unter "Arbeitet für"
// (ST-PTY-001, Signatur S2, Vorgabe DESIGN V1-V4).
//
// Eine Offenlegung wie "Kennungen, Herkunft und Datenstand": offen, sobald
// etwas erkannt ist, sonst zu und "(optional)". Die Felder sind der Vorschlag
// und zugleich die Korrektur; bestaetigt wird mit der einen Hauptaktion des
// Bildschirms. Die Form prueft der Server, seine Antwort steht am Feld
// (NFR-UX-005). Die Geschaeftsanschrift reicht der Bildschirm als `anschrift`
// herein: es sind dieselben Felder wie in der Anlage einer Person (X7).

import type { ReactElement, ReactNode } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Field,
  Input,
  makeStyles,
  Select,
  tokens,
} from '@fluentui/react-components';

import { REGISTER_ARTEN, type OrganisationAngaben, type RegisterArt } from '../organisation-angaben.ts';

const useStyles = makeStyles({
  felder: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  gruppe: {
    border: 'none',
    margin: 0,
    padding: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  legende: {
    padding: 0,
    fontWeight: tokens.fontWeightSemibold,
  },
  // Registerart und Nummer nebeneinander, bei 320 px untereinander.
  zeile: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    '> *': { flex: '1 1 10rem', minWidth: 0 },
  },
});

/** Der Titel der Offenlegung (V1). */
export function organisationAngabenTitel(erkannt: number): string {
  return erkannt > 0 ? `Angaben zur Organisation (${erkannt} erkannt)` : 'Angaben zur Organisation (optional)';
}

export const REGISTERART_FEHLT = 'Wähle die Registerart zur Nummer, zum Beispiel HRB.';

export const UST_ID_HINWEIS = 'Zwei Buchstaben und die Nummer, z. B. DE123456789.';

export function OrganisationAngabenAbschnitt({
  angaben,
  onChange,
  erkannt,
  offen,
  onOffenChange,
  fehlerAm,
  anschrift,
  anschriftAbgelehnt,
  disabled,
}: {
  readonly angaben: OrganisationAngaben;
  readonly onChange: (patch: Partial<OrganisationAngaben>) => void;
  /** Wie viele Angaben erkannt sind; bestimmt den Titel. */
  readonly erkannt: number;
  readonly offen: boolean;
  readonly onOffenChange: (offen: boolean) => void;
  /** Der Satz des Servers zu einem Feldpfad, sonst `null`. */
  readonly fehlerAm: (fieldPath: string) => string | null;
  /** Die Felder der Geschaeftsanschrift, wenn eine erkannt ist. */
  readonly anschrift?: ReactNode;
  /** An ihrer Stelle, wenn die erkannte Anschrift abgelehnt ist: der Rueckweg (B9). */
  readonly anschriftAbgelehnt?: ReactNode;
  readonly disabled: boolean;
}): ReactElement {
  const styles = useStyles();
  // Eine Nummer ohne Registerart lehnt der Server an der Nummer ab; zu tun ist es an der Art (DESIGN zu #773).
  const registerFehler = fehlerAm('commercialRegister.value');
  const fehltArt = angaben.registerArt === '' && angaben.registerNummer.trim() !== '';
  return (
    <Accordion
      collapsible
      openItems={offen ? ['angaben'] : []}
      onToggle={(_event, data) => onOffenChange(data.openItems.includes('angaben'))}
    >
      <AccordionItem value="angaben">
        <AccordionHeader as="h3">{organisationAngabenTitel(erkannt)}</AccordionHeader>
        <AccordionPanel>
          <div className={styles.felder}>
            {anschrift === undefined ? anschriftAbgelehnt : (
              <fieldset className={styles.gruppe}>
                <legend className={styles.legende}>Geschäftsanschrift</legend>
                {anschrift}
              </fieldset>
            )}
            <fieldset className={styles.gruppe}>
              <legend className={styles.legende}>Handelsregister</legend>
              <div className={styles.zeile}>
                <Field label="Registerart" validationMessage={fehltArt && registerFehler !== null ? REGISTERART_FEHLT : null}>
                  <Select
                    id="qc-org-register-art"
                    disabled={disabled}
                    value={angaben.registerArt}
                    onChange={(_event, data) => onChange({ registerArt: data.value as RegisterArt | '' })}
                  >
                    <option value="">Keine Angabe</option>
                    {REGISTER_ARTEN.map((art) => (
                      <option key={art} value={art}>
                        {art}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Nummer" validationMessage={fehltArt ? null : registerFehler}>
                  <Input
                    id="qc-org-register-nummer"
                    disabled={disabled}
                    value={angaben.registerNummer}
                    onChange={(_event, data) => onChange({ registerNummer: data.value })}
                  />
                </Field>
              </div>
              <Field label="Registergericht" validationMessage={fehlerAm('commercialRegister.issuer')}>
                <Input
                  id="qc-org-register-gericht"
                  disabled={disabled}
                  value={angaben.registerGericht}
                  onChange={(_event, data) => onChange({ registerGericht: data.value })}
                />
              </Field>
            </fieldset>
            <Field label="USt-IdNr." hint={UST_ID_HINWEIS} validationMessage={fehlerAm('vatId.value') ?? fehlerAm('vatId')}>
              <Input
                id="qc-org-ustid"
                disabled={disabled}
                value={angaben.ustId}
                onChange={(_event, data) => onChange({ ustId: data.value })}
              />
            </Field>
            <Field label="Website" validationMessage={fehlerAm('website')}>
              <Input
                id="qc-org-website"
                type="text"
                inputMode="url"
                autoComplete="url"
                disabled={disabled}
                value={angaben.website}
                onChange={(_event, data) => onChange({ website: data.value })}
              />
            </Field>
          </div>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
}
